import { useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useAuth, useOrganization } from "@clerk/react";
import { platformTrpc } from "../lib/platform-trpc";

/**
 * The platform profile as the API now returns it. Industry interest is no longer a
 * column on platform_users — per-provider sub-industry tags live behind
 * `platformTrpc.subIndustry.*` — so this is narrower than the old shared
 * `PlatformUserProfile`.
 */
export interface PlatformProfile {
  id: string;
  clerkUserId: string;
  email: string;
  firstName: string;
  lastName: string;
  companyName: string | null;
  status: string;
  primaryProviderTenantId: string | null;
}

export type PlatformAuthStatus =
  | "loading"
  | "unauthenticated" // no Clerk session
  | "no_account" // signed in but no platform_users row (or an org user)
  | "pending_verification"
  | "verified"
  | "migrated";

export interface PlatformAuthResult {
  isLoading: boolean;
  isSignedIn: boolean;
  platformUser: PlatformProfile | null;
  status: PlatformAuthStatus;
}

/**
 * Reads the current platform-user state (no redirects). `getProfile` only runs
 * once a Clerk session exists; with email-link verification there is no session
 * until the link is clicked, so before then this resolves to `unauthenticated`.
 */
export function usePlatformAuth(): PlatformAuthResult {
  const { isLoaded, isSignedIn } = useAuth();
  const { isLoaded: orgLoaded } = useOrganization();
  // Probe the platform profile whenever signed in — REGARDLESS of an active org. A
  // self-registered subscriber carries the provider's org but is ALSO a platform user;
  // `getProfile` resolves by clerkUserId (org-tolerant) and returns null for pure tenant
  // users, so the marketplace works without dropping the provider context.
  const enabled = isLoaded && Boolean(isSignedIn) && orgLoaded;
  const profile = platformTrpc.getProfile.useQuery(undefined, {
    enabled,
    retry: false,
  });

  // eslint-disable-next-line no-console -- temporary auth debug
  console.log("[auth-debug] usePlatformAuth", {
    isLoaded,
    isSignedIn,
    orgLoaded,
    enabled,
    profileStatus: profile.status,
    profileError: profile.error
      ? `${profile.error.data?.code ?? ""} ${profile.error.message}`
      : null,
  });

  let status: PlatformAuthStatus = "loading";
  if (isLoaded && !isSignedIn) {
    status = "unauthenticated";
  } else if (enabled) {
    if (profile.isLoading) status = "loading";
    else if (profile.error || !profile.data) status = "no_account";
    else status = profile.data.status as PlatformAuthStatus;
  }

  return {
    isLoading: !isLoaded || !orgLoaded || (enabled && profile.isLoading),
    isSignedIn: Boolean(isSignedIn),
    platformUser: profile.data ?? null,
    status,
  };
}

/**
 * Guard for /platform/* and /marketplace/* pages. Redirects:
 *   - unauthenticated  → /register
 *   - no platform row  → /register
 *   - pending_verify   → /platform/verify-email
 * When `requireVerified` is false, `pending_verification` is allowed through
 * (e.g. a page that itself prompts for verification).
 */
export function usePlatformGuard(
  opts: { requireVerified?: boolean } = {},
): PlatformAuthResult {
  const result = usePlatformAuth();
  const navigate = useNavigate();
  const requireVerified = opts.requireVerified ?? true;

  useEffect(() => {
    if (result.isLoading) return;
    if (result.status === "unauthenticated" || result.status === "no_account") {
      void navigate({ to: "/register" });
      return;
    }
    if (requireVerified && result.status === "pending_verification") {
      void navigate({ to: "/platform/verify-email" });
    }
  }, [result.isLoading, result.status, requireVerified, navigate]);

  return result;
}
